import React, { useEffect, useState } from "react";
import "./IndexPageLayout.css";

import useAuthStore from "../../Zustand/Store/useAuthStore";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import useEmployeeDataStore from "../../Zustand/Store/useEmployeeDataStore";
import { routes } from "../../Configurations/PermissionConfigrations";
import NotAllowed from "../../Exceptions/NotAllowed";
import usePermissionDataStore from "../../Zustand/Store/usePermissionDataStore";
import toast from "react-hot-toast";
import axios from "axios";
import { MAIN_URL, getApiHeaders } from "../../Configurations/Urls";
import {
  applyPreferredOrganizationToUserPayload,
  clearClientPreferredOrganizationId,
} from "../../Utils/clientActiveOrganization";
import {
  buildAttendanceLandingState,
  persistAttendanceLandingState,
  resolveOrganizationUserId,
  isInternLoginUser,
  clearAttendanceLandingState,
  navigateAfterManagerLogin,
  getAttendanceOrganizationsForUser,
  assertManagerAttendanceLoginAllowed,
  canAccessManagerAttendanceLogin,
  canAccessAttendanceLogin,
  shouldUseManagerKioskFlow,
  readAttendanceLandingState,
} from "../../Utils/attendanceAuth";
import { ensureAttendanceSession } from "../../Utils/attendanceSession";

export default function RouteProtector({ children }) {
  const { resetData } = useEmployeeDataStore();
  const { Permission, setPermission } = usePermissionDataStore();
  const navigate = useNavigate();
  const location = useLocation();

  const { logout, userData, setAccess, login, setMode, access, isLoggedIn } =
    useAuthStore();

  const [isAllowed, setisAllowed] = useState(true);
  const [permissionCheckComplete, setPermissionCheckComplete] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);

  const organizationUserId = resolveOrganizationUserId(userData);

  const clearAttendanceSession = () => {
    logout();
    clearClientPreferredOrganizationId();
    clearAttendanceLandingState();
    localStorage.removeItem("token");
  };

  useEffect(() => {
    const currentPath = location.pathname;
    localStorage.setItem("lastVisitedRoute", currentPath);

    if (!currentPath || !Permission?.length) {
      return;
    }

    const basePath = currentPath.includes("/edit")
      ? `${currentPath.split("/edit")[0]}/edit`
      : currentPath;

    const matchedRoute = routes.find((item) => item.name === basePath);

    if (!matchedRoute) {
      setisAllowed(true);
    } else if (
      matchedRoute.action &&
      Permission.includes(matchedRoute.action)
    ) {
      setisAllowed(true);
    } else {
      setisAllowed(false);
    }
  }, [location, Permission]);

  useEffect(() => {
    const bootstrapSession = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        setSessionReady(true);
        navigate("/login");
        return;
      }

      try {
        const storeUser = useAuthStore.getState().userData;

        let sessionUser = storeUser;
        if (
          storeUser?.application_user_id &&
          (storeUser.organizationuser?.organization_user_id ||
            (Array.isArray(storeUser.metadata) && storeUser.metadata.length > 0))
        ) {
          sessionUser = await ensureAttendanceSession(storeUser);
        } else {
          const { data } = await axios.get(`${MAIN_URL}/api/auth/token-login`, {
            headers: getApiHeaders(),
          });
          const user = applyPreferredOrganizationToUserPayload(data?.user);
          sessionUser = await ensureAttendanceSession(user);
        }

        const isManager = shouldUseManagerKioskFlow(sessionUser);

        if (isManager && !canAccessManagerAttendanceLogin(sessionUser)) {
          toast.error(
            assertManagerAttendanceLoginAllowed(sessionUser) ||
              "Manager attendance mode is enabled. Only admin users can access attendance login."
          );
          clearAttendanceSession();
          setSessionReady(true);
          navigate("/login");
          return;
        }

        if (!isManager && !canAccessAttendanceLogin(sessionUser)) {
          toast.error(
            isInternLoginUser(sessionUser)
              ? "Intern record not found. Attendance is for Intern users only."
              : "Employee record not found. Attendance is for Employee users only."
          );
          clearAttendanceSession();
          setSessionReady(true);
          navigate("/login");
          return;
        }

        setAccess(sessionUser?.role?.system_role_name);
        login(sessionUser);
        setMode(isInternLoginUser(sessionUser) ? 2 : 1);

        if (isManager) {
          const landingState = readAttendanceLandingState();
          const hasOrgContext =
            location.state?.organization_id || landingState?.organization_id;

          if (location.pathname === "/login") {
            navigateAfterManagerLogin(sessionUser, navigate);
          } else if (
            !hasOrgContext &&
            location.pathname !== "/organization-toggle"
          ) {
            navigateAfterManagerLogin(sessionUser, navigate);
          }
        } else {
          const orgs = getAttendanceOrganizationsForUser(sessionUser);
          const landingState = readAttendanceLandingState();
          const hasOrgContext =
            location.state?.organization_id || landingState?.organization_id;

          if (!hasOrgContext) {
            if (orgs.length === 1) {
              const nextState = buildAttendanceLandingState(sessionUser);
              if (nextState) {
                persistAttendanceLandingState(nextState);
                navigate("/", { state: nextState, replace: true });
              }
            } else if (
              orgs.length > 1 &&
              location.pathname !== "/organization-toggle"
            ) {
              navigate("/organization-toggle", { replace: true });
            }
          } else if (
            location.pathname === "/login" ||
            location.pathname === "/organization-toggle"
          ) {
            const nextState =
              buildAttendanceLandingState(sessionUser) || landingState;
            if (nextState) {
              persistAttendanceLandingState(nextState);
              navigate("/", { state: nextState, replace: true });
            }
          }
        }
      } catch (error) {
        if (error?.code === "NO_EMPLOYEE_ORG") {
          toast.error(
            "You are not registered as an Employee in any organization."
          );
        } else if (error?.code === "NO_INTERN_ORG") {
          toast.error(
            "You are not registered as an Intern in any organization."
          );
        } else if (error?.code === "NO_SCOPED_ORG") {
          toast.error(
            "No organizations found for your user type in this account."
          );
        } else if (error?.code === "NO_CLIENT_ORG") {
          toast.error("No organizations found for this account.");
        } else {
          console.log("error in attendance session bootstrap", error);
        }
        clearAttendanceSession();
        navigate("/login");
      } finally {
        setSessionReady(true);
      }
    };

    bootstrapSession();
  }, []);

  useEffect(() => {
    const currentPath = location.pathname;
    if (
      currentPath === "/organization/employee/employee-details/add" ||
      currentPath === "/organization/employee/employee-details"
    ) {
      resetData();
    }
  }, [location.pathname, resetData]);

  useEffect(() => {
    const checkPermission = async () => {
      setPermissionCheckComplete(false);

      if (!organizationUserId) {
        setPermission([]);
        setPermissionCheckComplete(true);
        return;
      }

      try {
        const res = await axios.get(
          `${MAIN_URL}/api/user/${organizationUserId}/check-permission-access`,
          { headers: getApiHeaders() }
        );
        setPermission(res?.data?.allowed_actions || []);
      } catch (error) {
        if (error.response && error.response.status === 401) {
          toast.error("Session Expired!");
          clearAttendanceSession();
          window.location.href = "/login";
        }
        console.error("Permission check error:", error);
        setPermission([]);
      } finally {
        setPermissionCheckComplete(true);
      }
    };

    if (userData && organizationUserId) {
      checkPermission();
    } else if (userData && !organizationUserId) {
      setPermission([]);
      setPermissionCheckComplete(true);
    }
  }, [userData, organizationUserId, setPermission]);

  const currentPath = location.pathname;

  if (access === "Admin" && currentPath === "/sidebar-option-page-1") {
    return <Navigate to="/" replace />;
  }

  if (!sessionReady && localStorage.getItem("token")) {
    return null;
  }

  if (permissionCheckComplete && !isAllowed) {
    return <NotAllowed />;
  }

  if (!isLoggedIn && !localStorage.getItem("token")) {
    return <Navigate to="/login" replace />;
  }

  return children;
}
